"use client";
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function OTPVerification() {
  const navigate = useNavigate();
  const { verifyOTP, forgotPassword } = useAuth(); // <-- added forgotPassword()
  const location = useLocation();
  const { token, email } = location.state || {};

  const [otp, setOTP] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  // ---------------------------
  // Handle OTP Submit
  // ---------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setIsLoading(true);

    try {
      const result = await verifyOTP(otp, token);

      if (result.status == 200) {
        navigate("/ResetPassword", { state: { email, token } });
      } else {
        setErrorMessage(result.message || "Invalid OTP. Please try again.");
      }
    } catch (err) {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------------------
  // Handle Resend OTP
  // ---------------------------
  const handleResendOTP = async () => {
    setResendMessage("");
    setResendLoading(true);

    try {
      const response = await forgotPassword(email);

      if (response.success) {
        setResendMessage("OTP resent successfully!");
      } else {
        setResendMessage("Failed to resend OTP.");
      }
    } catch (err) {
      setResendMessage("Something went wrong. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4 relative overflow-x-hidden overflow-y-auto">
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-4 shadow-lg ring-2 ring-blue-500/30">
            <img
              src="https://cdn.acsdev.in/fynryx/acs.png"
              alt="ACS Logo"
              className="w-12 h-12 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">OTP Verification</h1>
          <p className="text-gray-600">Enter the 6-digit OTP sent to your email</p>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 relative">

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="otp" className="block text-sm font-semibold text-gray-700">
                OTP
              </label>
              <input
                id="otp"
                type="text"
                maxLength={6}
                value={otp}
                onChange={(e) => setOTP(e.target.value)}
                className="block w-full pl-3 pr-3 py-3 border border-gray-300 rounded-xl bg-white/50 backdrop-blur-sm"
                placeholder="Enter OTP"
              />
            </div>

            {errorMessage && (
              <div className="text-center text-sm font-medium text-red-500">
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-500 to-indigo-600"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </div>
              ) : (
                "Verify OTP"
              )}
            </button>
          </form>

          {/* Resend Section */}
          <div className="mt-6 text-center text-sm text-gray-600">
            <button
              type="button"
              onClick={handleResendOTP}
              disabled={resendLoading}
              className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200"
            >
              {resendLoading ? "Resending..." : "Resend OTP"}
            </button>

            {resendMessage && (
              <div className="mt-2 text-sm font-medium text-green-600">{resendMessage}</div>
            )}
          </div>

          <div className="mt-4 text-center text-sm text-gray-600">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200"
            >
              Back to Login
            </button>
          </div>
        </div>

        <footer className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            © 2025 Algorithmica Consultancy Services PVT LTD.
          </p>
          <p className="text-xs text-gray-500">All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
